import { Link, useLocation } from "react-router";
import { cn } from "@/components/ui";

interface NavItem {
  href: string;
  label: string;
  /** Also highlight for these path prefixes — a case lives under Inbox. */
  owns?: string[];
}

/**
 * Global navigation is deliberately short. Documents, financials, risk and
 * models are views of a case, not destinations — exposing them here would
 * invite analysts to work outside case context, which is the failure mode the
 * product is designed against.
 */
const PRIMARY: NavItem[] = [{ href: "/inbox", label: "Inbox", owns: ["/cases", "/borrowers"] }];
const SECONDARY: NavItem[] = [
  { href: "/reports", label: "Reports" },
  { href: "/integrations", label: "Integrations" },
];
const TERTIARY: NavItem[] = [
  { href: "/billing", label: "Billing" },
  { href: "/admin", label: "Admin" },
];

function NavLink({ item, pathname }: { item: NavItem; pathname: string }) {
  const active =
    pathname === item.href ||
    pathname.startsWith(`${item.href}/`) ||
    (item.owns?.some((prefix) => pathname.startsWith(prefix)) ?? false);

  return (
    <Link
      to={item.href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "block rounded-md px-2.5 py-1.5 text-sm transition-colors",
        active ? "bg-accent-soft font-medium text-accent" : "text-ink-muted hover:bg-surface-sunken hover:text-ink",
      )}
    >
      {item.label}
    </Link>
  );
}

export function Sidebar() {
  const pathname = useLocation().pathname;

  return (
    <nav aria-label="Main" className="flex h-full w-52 shrink-0 flex-col border-r border-line bg-surface">
      <Link to="/inbox" className="flex items-center gap-2 px-4 py-4">
        <span className="grid size-6 place-items-center rounded bg-accent text-[0.625rem] font-bold text-white">
          CU
        </span>
        <span className="text-sm font-semibold tracking-tight">Underwriting</span>
      </Link>

      <div className="space-y-1 px-2">
        {PRIMARY.map((item) => (
          <NavLink key={item.href} item={item} pathname={pathname} />
        ))}
      </div>

      <hr className="mx-4 my-3 border-line" />

      <div className="space-y-1 px-2">
        {SECONDARY.map((item) => (
          <NavLink key={item.href} item={item} pathname={pathname} />
        ))}
      </div>

      <hr className="mx-4 my-3 border-line" />

      <div className="space-y-1 px-2">
        {TERTIARY.map((item) => (
          <NavLink key={item.href} item={item} pathname={pathname} />
        ))}
      </div>

      <div className="flex-1" />
    </nav>
  );
}
